<script setup lang="ts">
import IconCheckCircle from '@/domains/common/components/icons/IconCheckCircle.vue';
import IconWarning from '@/domains/common/components/icons/IconWarning.vue';
import NotificationStatus from '@/domains/common/typescript/NotificationStatus';
import { XCircleIcon, XMarkIcon, InformationCircleIcon } from '@heroicons/vue/20/solid';
import { ref, shallowRef, watchEffect } from 'vue';

const emit = defineEmits<{ (e: 'hide-message'): void }>();

const props = defineProps<{
    message: string;
    status: NotificationStatus;
    count?: number;
}>();

const WarningIcon = shallowRef(IconWarning);

const textColor = ref('');
const borderColor = ref('');
const progressBackgroundColor = ref('');
const backgroundColor = ref('');
const icon = ref();

watchEffect(() => {
    switch (props.status) {
        case NotificationStatus.success:
            textColor.value = 'text-toaster-success';
            borderColor.value = 'border-toaster-success';
            progressBackgroundColor.value = 'bg-toaster-success/50';
            backgroundColor.value = 'bg-toaster-success';
            icon.value = IconCheckCircle;
            break;
        case NotificationStatus.warning:
            textColor.value = 'text-toaster-warning';
            borderColor.value = 'border-toaster-warning';
            progressBackgroundColor.value = 'bg-warning/50';
            backgroundColor.value = 'bg-warning';
            icon.value = WarningIcon;
            break;
        case NotificationStatus.error:
            textColor.value = 'text-toaster-error';
            borderColor.value = 'border-toaster-error';
            progressBackgroundColor.value = 'bg-toaster-error/50';
            backgroundColor.value = 'bg-toaster-error';
            icon.value = XCircleIcon;
            break;
        case NotificationStatus.info:
            textColor.value = 'text-toaster-info';
            borderColor.value = 'border-toaster-info';
            progressBackgroundColor.value = 'bg-toaster-info/50';
            backgroundColor.value = 'bg-toaster-info';
            icon.value = InformationCircleIcon;
            break;
        default:
            textColor.value = 'text-gray-900';
            borderColor.value = 'border-gray-900';
            progressBackgroundColor.value = 'bg-gray-900/50';
            backgroundColor.value = 'bg-gray-900/50';
            icon.value = null;
    }
});
</script>
<template>
    <div
        :class="[
            'pointer-events-auto max-w-md overflow-hidden rounded-2xl border-2 bg-white shadow-lg ring-1 ring-black/20',
            textColor,
            borderColor,
        ]"
    >
        <div class="flex h-16 flex-row gap-0">
            <div
                v-if="count && count > 1"
                :class="[
                    'flex h-full w-10 flex-row items-center justify-center text-2xl font-bold text-white',
                    backgroundColor,
                ]"
            >
                <span>{{ count }}</span>
            </div>
            <div class="flex grow flex-row items-center gap-4 px-4 pt-3 pb-2">
                <div class="shrink-0">
                    <component :is="icon" class="h-6 w-6" />
                </div>
                <div class="w-content flex-1 pt-0.5">
                    <p class="text-lg font-semibold">
                        {{ props.message }}
                    </p>
                </div>
                <div class="flex shrink-0">
                    <button
                        type="button"
                        :class="['inline-flex rounded-full bg-transparent p-1 transition-colors']"
                        @click="emit('hide-message')"
                    >
                        <span class="sr-only">{{ $t('notification.close') }}</span>
                        <XMarkIcon class="h-6 w-6" />
                    </button>
                </div>
            </div>
        </div>
        <div :class="['animate-countdown-width h-1.5', progressBackgroundColor]" />
    </div>
</template>
